import { cn } from "@shared/lib/utils";

export type StatPairProps = {
  /** 12px low-chroma label — numbers never appear alone (§1.1, §1.2) */
  label: string;
  /** number → en-US grouping; null/undefined → em-dash (§1.2) */
  value?: string | number | null;
  /** unit suffix, e.g. "RP", "IP", "cards" (§1.2) */
  unit?: string;
  /** signed delta: explicit +/− sign, + = gold, − = neutral ink — never red (§1.2) */
  signed?: boolean;
  align?: "left" | "right";
  className?: string;
};

/** §1.2 numeric grammar — label + Geist Mono tnum value + unit suffix pair. */
export function StatPair({ label, value, unit, signed, align = "left", className }: StatPairProps) {
  const missing = value == null || value === "";
  const num = typeof value === "number" ? value : null;
  let text: string;
  if (missing) {
    text = "—";
  } else if (num != null) {
    text = signed
      ? `${num < 0 ? "−" : "+"}${Math.abs(num).toLocaleString("en-US")}`
      : num.toLocaleString("en-US");
  } else {
    text = String(value);
  }
  const positive =
    !missing && signed && (num != null ? num > 0 : String(value).startsWith("+"));

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-0.5",
        align === "right" && "items-end text-right",
        className,
      )}
    >
      <span className="text-xs text-ink-mute">{label}</span>
      <span
        className={cn(
          "font-mono text-sm font-medium tabular-nums",
          positive ? "text-accent" : "text-ink",
        )}
      >
        {text}
        {unit && !missing && <span className="ml-1 font-normal text-ink-mute">{unit}</span>}
      </span>
    </div>
  );
}
